import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import EmployeeLookup from "@/components/EmployeeLookup";
import {
  IdCard,
  Save,
  FileDown,
  Loader2,
  AlertCircle,
  Check,
  RectangleHorizontal,
  RectangleVertical,
} from "lucide-react";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

const DEFAULT_SETTINGS = {
  id: "standard",
  name: "Standard Employee",
  orientation: "horizontal",
  layout_style: "classic",
  photo_position: "left",
  text_align: "left",
  font_family: "default",
  name_font_size: 9,
  detail_font_size: 6.5,
  code_font_size: 8,
  company_font_size: 7,
  text_offset_x_mm: 0,
  text_offset_y_mm: 0,
  accent_color: "#226DB4",
  intern_accent_color: "#0284c7",
  show_department: true,
  show_designation: true,
  show_photo: true,
  show_logo: true,
  show_qr: true,
  show_employment_type_badge: true,
  company_name_source: "name",
};

const FONT_OPTIONS = [
  { id: "default", label: "Sans (default)", css: "ui-sans-serif, system-ui, sans-serif" },
  { id: "serif", label: "Serif", css: "ui-serif, Georgia, serif" },
  { id: "mono", label: "Mono", css: "ui-monospace, Consolas, monospace" },
  { id: "calibri", label: "Calibri-like", css: "Calibri, Candara, Segoe UI, sans-serif" },
];

const LAYOUT_OPTIONS = [
  { id: "classic", label: "Classic" },
  { id: "header_band", label: "Header band" },
  { id: "centered", label: "Centered" },
];

function displayName(emp) {
  const b = emp?.basic || {};
  return [b.first_name, b.middle_name, b.last_name].filter(Boolean).join(" ") || "Employee";
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-2 text-sm text-slate-700">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="h-4 w-4 rounded border-slate-300 text-[#226DB4] focus:ring-[#226DB4]"
      />
    </label>
  );
}

function ChoiceCard({ active, onClick, icon: Icon, title, subtitle, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition w-full ${
        active
          ? "border-[#226DB4] bg-[#E8F0FF] ring-1 ring-[#226DB4]/40"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <span
        className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
          active ? "border-[#226DB4] bg-[#226DB4]" : "border-slate-300 bg-white"
        }`}
      >
        {active && <Check className="h-3 w-3 text-white" />}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
          {Icon && <Icon className="h-4 w-4 text-slate-500" />}
          {title}
        </span>
        {subtitle && <span className="block text-[11px] text-slate-500 mt-0.5">{subtitle}</span>}
      </span>
    </button>
  );
}

function CardPreview({ emp, company, depMap, desigMap, settings }) {
  if (!emp) {
    return (
      <div className="h-48 flex items-center justify-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg bg-slate-50">
        Select an employee to preview
      </div>
    );
  }
  const isIntern = emp.employment?.employment_type === "Intern";
  const accent = isIntern ? settings.intern_accent_color : settings.accent_color;
  const name = displayName(emp);
  const code = emp.basic?.code || "—";
  const designation = desigMap[emp.employment?.designation_id]?.title || "";
  const department = depMap[emp.employment?.department_id]?.name || "";
  const companyName =
    settings.company_name_source === "legal_name"
      ? (company?.legal_name || company?.name || "Company")
      : (company?.name || company?.legal_name || "Company");
  const photo = emp.basic?.photo_url;
  const initial = (emp.basic?.first_name || name || "E").charAt(0).toUpperCase();
  const vertical = settings.orientation === "vertical";
  const style = settings.layout_style || "classic";
  const stacked = vertical || style === "centered" || settings.photo_position === "top";
  const photoRight = !stacked && settings.photo_position === "right";
  const align = settings.text_align || "left";
  const fontCss = FONT_OPTIONS.find((f) => f.id === settings.font_family)?.css || FONT_OPTIONS[0].css;
  const textAlignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  const namePx = Math.round(Number(settings.name_font_size || 9) * 1.6);
  const detailPx = Math.round(Number(settings.detail_font_size || 6.5) * 1.55);

  const Photo = (
    settings.show_photo ? (
      <div
        className={`${stacked ? "h-20 w-20 mb-2" : "h-24 w-24 shrink-0 self-center"} rounded-md overflow-hidden border-2 flex items-center justify-center`}
        style={{ borderColor: accent, background: "#E8F0FF" }}
      >
        {photo ? (
          <img src={photo} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-2xl font-semibold" style={{ color: accent }}>{initial}</span>
        )}
      </div>
    ) : null
  );

  return (
    <div className="flex justify-center py-4">
      <div
        className={`relative bg-white shadow-md overflow-hidden border border-slate-200 rounded-xl ${
          vertical ? "w-[220px] aspect-[54/85.6]" : "w-full max-w-[420px] aspect-[85.6/54]"
        }`}
        data-testid="id-card-preview"
        style={{
          fontFamily: fontCss,
          ...(!stacked && style === "classic"
            ? photoRight
              ? { borderRightWidth: 10, borderRightColor: accent }
              : { borderLeftWidth: 10, borderLeftColor: accent }
            : {}),
        }}
      >
        {style === "header_band" && (
          <div className="absolute top-0 left-0 right-0 h-10 flex items-center justify-center px-3" style={{ background: accent }}>
            <span className="text-[11px] font-semibold text-white truncate">{companyName}</span>
          </div>
        )}
        {(stacked && style !== "header_band") && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }} />
            <div className="absolute bottom-0 left-0 right-0 h-1.5" style={{ background: accent }} />
          </>
        )}

        {stacked ? (
          <div className={`absolute inset-0 flex flex-col items-center px-4 ${style === "header_band" ? "pt-12" : "pt-5"} pb-4 ${textAlignCls}`}>
            {style !== "header_band" && (
              <div className="text-[10px] font-semibold text-slate-800 truncate w-full mb-2">{companyName}</div>
            )}
            {settings.show_employment_type_badge && isIntern && (
              <span className="mb-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-sky-100 text-sky-800">Intern</span>
            )}
            {Photo}
            <div className="font-semibold text-slate-900 leading-tight w-full" style={{ fontSize: namePx }}>{name}</div>
            {settings.show_designation && designation && (
              <div className="text-slate-600 mt-0.5 w-full" style={{ fontSize: detailPx }}>{designation}</div>
            )}
            {settings.show_department && department && (
              <div className="text-slate-500 w-full" style={{ fontSize: detailPx - 1 }}>{department}</div>
            )}
            <div className="mt-2 font-bold w-full" style={{ color: accent, fontSize: namePx - 2 }}>{code}</div>
          </div>
        ) : (
          <div className={`absolute inset-0 p-4 flex gap-4 ${style === "header_band" ? "pt-12" : ""} ${photoRight ? "flex-row-reverse" : ""}`}>
            {Photo}
            <div className={`flex-1 min-w-0 flex flex-col justify-center ${textAlignCls}`}>
              {style !== "header_band" && (
                <div className="text-[11px] font-semibold text-slate-800 truncate mb-1">{companyName}</div>
              )}
              {settings.show_employment_type_badge && isIntern && (
                <span className="inline-flex w-fit mb-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-sky-100 text-sky-800">
                  Intern
                </span>
              )}
              <div className="font-semibold text-slate-900 truncate leading-tight" style={{ fontSize: namePx }}>{name}</div>
              {settings.show_designation && designation && (
                <div className="text-slate-600 truncate mt-0.5" style={{ fontSize: detailPx }}>{designation}</div>
              )}
              {settings.show_department && department && (
                <div className="text-slate-500 truncate" style={{ fontSize: detailPx - 1 }}>{department}</div>
              )}
              <div className="mt-2 font-bold tracking-wide" style={{ color: accent, fontSize: namePx - 2 }}>{code}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function IdCardTemplates() {
  const [pack, setPack] = useState({ active_id: "standard", templates: [DEFAULT_SETTINGS] });
  const [activeId, setActiveId] = useState("standard");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSnap, setSavedSnap] = useState("");
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [previewEmpId, setPreviewEmpId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const applyPack = (data) => {
    const next = {
      active_id: data?.active_id || "standard",
      templates: (data?.templates || []).map((t) => ({ ...DEFAULT_SETTINGS, ...t })),
    };
    if (!next.templates.length) next.templates = [{ ...DEFAULT_SETTINGS }];
    setPack(next);
    const cur = next.templates.find((t) => t.id === next.active_id) || next.templates[0];
    setActiveId(cur.id);
    setSettings({ ...DEFAULT_SETTINGS, ...cur });
    setSavedSnap(JSON.stringify({ active_id: next.active_id, templates: next.templates }));
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [tpl, emps, deps, desigs, cos] = await Promise.all([
        api.get("/id-card-template"),
        api.get("/employees"),
        api.get("/departments"),
        api.get("/designations"),
        api.get("/companies").catch(() => ({ data: [] })),
      ]);
      applyPack(tpl.data);
      const list = (emps.data || []).filter((e) => e.employment?.status !== "Inactive");
      setEmployees(list);
      setDepartments(deps.data || []);
      setDesignations(desigs.data || []);
      setCompanies(cos.data || []);
      if (!previewEmpId && list.length) setPreviewEmpId(list[0].id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load ID card settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const depMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const desigMap = useMemo(() => Object.fromEntries(designations.map((d) => [d.id, d])), [designations]);
  const previewEmp = useMemo(() => employees.find((e) => e.id === previewEmpId), [employees, previewEmpId]);
  const previewCompany = useMemo(() => {
    if (!previewEmp) return companies[0] || null;
    return companies.find((c) => c.id === previewEmp.company_id) || companies[0] || null;
  }, [previewEmp, companies]);

  const dirtyPack = useMemo(() => {
    const templates = pack.templates.map((t) => (t.id === activeId ? { ...settings, id: activeId } : t));
    return JSON.stringify({ active_id: activeId, templates }) !== savedSnap;
  }, [pack.templates, activeId, settings, savedSnap]);

  const set = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const switchTemplate = (id) => {
    if (id === activeId) return;
    // Keep unsaved edits for current template in local pack
    const templates = pack.templates.map((t) => (t.id === activeId ? { ...settings, id: activeId } : t));
    const next = templates.find((t) => t.id === id) || templates[0];
    setPack({ active_id: id, templates });
    setActiveId(id);
    setSettings({ ...DEFAULT_SETTINGS, ...next });
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    setOkMsg("");
    try {
      const templates = pack.templates.map((t) => (t.id === activeId ? { ...settings, id: activeId, name: settings.name } : t));
      const r = await api.put("/id-card-template", { active_id: activeId, templates });
      applyPack(r.data);
      setOkMsg("ID card templates saved");
      setTimeout(() => setOkMsg(""), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!previewEmpId) return;
    setDownloading(true);
    setErr("");
    try {
      const r = await api.post(
        "/id-cards/preview.pdf",
        { employee_id: previewEmpId, template_id: activeId, settings },
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ID-Card-${previewEmp?.basic?.code || "employee"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminLayout title="ID Card Templates" breadcrumbs={["Home", "Settings", "ID Card Templates"]}>
      {err && (
        <div className="mb-4 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {okMsg && (
        <div className="mb-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
          <Check className="h-4 w-4" /> {okMsg}
        </div>
      )}

      <div className="mb-4 rounded-lg border border-sky-200/80 bg-sky-50/50 px-4 py-3 text-sm text-slate-600">
        Pick a named template from the dropdown, then adjust orientation, fonts, text position, and fields.
        Save makes that template the company default for downloads.
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-5">
            <div className="flex items-center gap-2 text-slate-800 font-semibold">
              <IdCard className="h-5 w-5 text-[#226DB4]" />
              Template settings
            </div>

            <label className="block text-xs text-slate-600 space-y-1">
              <span className="font-medium">Template</span>
              <select
                className={inputCls}
                value={activeId}
                onChange={(e) => switchTemplate(e.target.value)}
                data-testid="id-card-template-select"
              >
                {pack.templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name || t.id}</option>
                ))}
              </select>
            </label>

            <label className="block text-xs text-slate-600 space-y-1">
              <span className="font-medium">Template display name</span>
              <input
                className={inputCls}
                value={settings.name || ""}
                onChange={(e) => set("name", e.target.value)}
                maxLength={60}
              />
            </label>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Orientation</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <ChoiceCard
                  active={settings.orientation === "horizontal"}
                  onClick={() => set("orientation", "horizontal")}
                  icon={RectangleHorizontal}
                  title="Horizontal"
                  subtitle="85.6 × 54 mm"
                  testid="id-card-orient-horizontal"
                />
                <ChoiceCard
                  active={settings.orientation === "vertical"}
                  onClick={() => set("orientation", "vertical")}
                  icon={RectangleVertical}
                  title="Vertical"
                  subtitle="54 × 85.6 mm"
                  testid="id-card-orient-vertical"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Layout style</span>
                <select className={inputCls} value={settings.layout_style} onChange={(e) => set("layout_style", e.target.value)}>
                  {LAYOUT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Photo position</span>
                <select className={inputCls} value={settings.photo_position} onChange={(e) => set("photo_position", e.target.value)} data-testid="id-card-photo-position">
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="top">Top (stacked)</option>
                </select>
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Text alignment</span>
                <select className={inputCls} value={settings.text_align} onChange={(e) => set("text_align", e.target.value)} data-testid="id-card-text-align">
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Font</span>
                <select className={inputCls} value={settings.font_family} onChange={(e) => set("font_family", e.target.value)} data-testid="id-card-font">
                  {FONT_OPTIONS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Name size</span>
                <input type="number" min={6} max={14} step={0.5} className={inputCls} value={settings.name_font_size} onChange={(e) => set("name_font_size", e.target.value)} />
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Detail size</span>
                <input type="number" min={5} max={11} step={0.5} className={inputCls} value={settings.detail_font_size} onChange={(e) => set("detail_font_size", e.target.value)} />
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Code size</span>
                <input type="number" min={6} max={12} step={0.5} className={inputCls} value={settings.code_font_size} onChange={(e) => set("code_font_size", e.target.value)} />
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Company size</span>
                <input type="number" min={5} max={11} step={0.5} className={inputCls} value={settings.company_font_size} onChange={(e) => set("company_font_size", e.target.value)} />
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Text shift X (mm)</span>
                <input type="number" min={-8} max={8} step={0.5} className={inputCls} value={settings.text_offset_x_mm} onChange={(e) => set("text_offset_x_mm", e.target.value)} />
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Text shift Y (mm)</span>
                <input type="number" min={-8} max={8} step={0.5} className={inputCls} value={settings.text_offset_y_mm} onChange={(e) => set("text_offset_y_mm", e.target.value)} />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Employee accent</span>
                <input type="color" className="w-full h-9 border border-slate-200 rounded cursor-pointer" value={settings.accent_color} onChange={(e) => set("accent_color", e.target.value)} />
              </label>
              <label className="block text-xs text-slate-600 space-y-1">
                <span className="font-medium">Intern accent</span>
                <input type="color" className="w-full h-9 border border-slate-200 rounded cursor-pointer" value={settings.intern_accent_color} onChange={(e) => set("intern_accent_color", e.target.value)} />
              </label>
            </div>

            <label className="block text-xs text-slate-600 space-y-1">
              <span className="font-medium">Company name on card</span>
              <select className={inputCls} value={settings.company_name_source} onChange={(e) => set("company_name_source", e.target.value)}>
                <option value="name">Company display name</option>
                <option value="legal_name">Legal name</option>
              </select>
            </label>

            <div className="border-t border-slate-100 pt-2 divide-y divide-slate-50">
              <Toggle label="Show photo" checked={settings.show_photo} onChange={(v) => set("show_photo", v)} />
              <Toggle label="Show designation" checked={settings.show_designation} onChange={(v) => set("show_designation", v)} />
              <Toggle label="Show department" checked={settings.show_department} onChange={(v) => set("show_department", v)} />
              <Toggle label="Show company logo mark" checked={settings.show_logo} onChange={(v) => set("show_logo", v)} />
              <Toggle label="Show QR (employee code)" checked={settings.show_qr} onChange={(v) => set("show_qr", v)} />
              <Toggle label="Show INTERN badge for interns" checked={settings.show_employment_type_badge} onChange={(v) => set("show_employment_type_badge", v)} />
            </div>

            <button
              type="button"
              onClick={save}
              disabled={!dirtyPack || saving}
              data-testid="id-card-save"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#226DB4] hover:bg-[#1B5A94] rounded disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save templates
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-semibold text-slate-800">Preview & download</div>
              <button
                type="button"
                onClick={downloadPdf}
                disabled={!previewEmpId || downloading}
                data-testid="id-card-download-pdf"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#226DB4] hover:bg-[#1B5A94] rounded disabled:opacity-50"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                Download PDF
              </button>
            </div>

            <label className="block text-xs text-slate-600 space-y-1">
              <span className="font-medium">Preview employee</span>
              <EmployeeLookup
                employees={employees}
                value={previewEmpId}
                onChange={setPreviewEmpId}
                placeholder="Search employee…"
                testid="id-card-employee-lookup"
              />
            </label>

            <CardPreview
              emp={previewEmp}
              company={previewCompany}
              depMap={depMap}
              desigMap={desigMap}
              settings={settings}
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
